import { ENDPOINTS } from '@/constants/api'
import { QUERY_KEYS } from '@/constants/query-keys'
import type { QueryOf, RequestBodyOf, ResponseData } from '@/types/api'
import { apiSlice } from './base'

export type Report = ResponseData<'/api/v1/reports/{id}', 'get'>
export type ReportRunsData = ResponseData<'/api/v1/reports/{id}/runs', 'get'>
export type ReportRun = ReportRunsData['items'][number]
export type DownloadData = ResponseData<'/api/v1/reports/{id}/runs/{runId}/download', 'get'>
type ReportsListData = ResponseData<'/api/v1/reports/', 'get'>
type ReportsQuery = QueryOf<'/api/v1/reports/', 'get'>
type CreateReportBody = RequestBodyOf<'/api/v1/reports/', 'post'>
type UpdateReportBody = RequestBodyOf<'/api/v1/reports/{id}', 'patch'>

export const reportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getReports: builder.query<ReportsListData, ReportsQuery | void>({
      query: (params) => ({ url: ENDPOINTS.REPORTS.ROOT, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: ReportsListData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((r) => ({ type: QUERY_KEYS.REPORT, id: r.id }) as const),
              { type: QUERY_KEYS.REPORT, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.REPORT, id: 'LIST' } as const],
    }),
    getReportById: builder.query<Report, string>({
      query: (id) => ENDPOINTS.REPORTS.BY_ID(id),
      transformResponse: (response: { data: Report }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.REPORT, id }],
    }),
    getReportRuns: builder.query<ReportRunsData, string>({
      query: (id) => ENDPOINTS.REPORTS.RUNS(id),
      transformResponse: (response: { data: ReportRunsData }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.REPORT_RUN, id }],
    }),
    createReport: builder.mutation<Report, CreateReportBody>({
      query: (body) => ({ url: ENDPOINTS.REPORTS.ROOT, method: 'POST', body }),
      transformResponse: (response: { data: Report }) => response.data,
      invalidatesTags: [{ type: QUERY_KEYS.REPORT, id: 'LIST' }],
    }),
    updateReport: builder.mutation<Report, { id: string; body: UpdateReportBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.REPORTS.BY_ID(id), method: 'PATCH', body }),
      transformResponse: (response: { data: Report }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.REPORT, id },
        { type: QUERY_KEYS.REPORT, id: 'LIST' },
      ],
    }),
    deleteReport: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.REPORTS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: QUERY_KEYS.REPORT, id },
        { type: QUERY_KEYS.REPORT, id: 'LIST' },
      ],
    }),
    // generate has no request body per the API spec
    generateReport: builder.mutation<ReportRun, string>({
      query: (id) => ({ url: ENDPOINTS.REPORTS.GENERATE(id), method: 'POST' }),
      transformResponse: (response: { data: ReportRun }) => response.data,
      invalidatesTags: (_r, _e, id) => [{ type: QUERY_KEYS.REPORT_RUN, id }],
    }),
    getReportDownload: builder.query<DownloadData, { id: string; runId: string }>({
      query: ({ id, runId }) => ENDPOINTS.REPORTS.DOWNLOAD(id, runId),
      transformResponse: (response: { data: DownloadData }) => response.data,
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetReportsQuery,
  useGetReportByIdQuery,
  useGetReportRunsQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useGenerateReportMutation,
  useGetReportDownloadQuery,
} = reportsApi
